import { useContext, useState, useMemo } from "react";
import ActivityService from "../../../../services/ActivityService";
import CompanyService from "../../../../services/CompanyService";
import GroupService from "../../../../services/GroupService";
import ToastContext from "../../../shared/bootstrap/Toast";
import { TranslationService } from "../../../../services/TranslationService";
import { ActivityGetResponse } from "../entities/AcitivityGetResponse";
import {
  parseDate,
  restTimezone,
  addTimezone
} from "../../../../utils/ParseUtils";
import { OptionalMap } from "../../../../utils/Utils";
import {
  CacheEntity,
  StorageService
} from "../../../../services/StorageService";
import { DisplayFileIcon } from "../ActivityHistory";
import { ActivityHistoryType } from "../../../shared/enums";
import { ActivityPendingResponse } from "../entities/AcitivityPendingResponse";
import { formatDateTimeDigits } from "../../../../utils/FormatUtils";
import TooltipComponent from "../../../shared/TooltipComponent";
import TagsIconDropdown from "../../../../components/TagsIconDropdown";
import ActivityTypeIconDropdown from "../../../../components/ActivityTypeIconDropdown";
import GroupIconDropdown from "../../../../components/GroupIconDropdown";
import "../../../shared/components/floatingStyles.css";
import { SafeHtml } from "../../../../components/SafeHtml";
import DynamicIcon from "../../../../components/DynamicIcon";
import { DateTimePickerEditor } from "../../../shared/components/Editors";
import FloatingPanelService from "../../../shared/FloatingPanel";
import MailViewHistoryDropdown from "./MailViewHistoryDropdown";

interface MailViewUser {
  Date: string;
  UserId: number;
  UserFullname: string;
}

class EmailEditModel {
  id: number;
  date: string;
  groupId?: string;
  tags?: string;
  activityTypeId?: string;

  constructor(email: ActivityGetResponse.Item) {
    const tags = CompanyService.getTags();
    this.id = email.MessageID;
    this.date =
      OptionalMap(parseDate(email.date), (x) => restTimezone(x).toString()) ??
      "";
    this.tags = email.tagList
      ?.split(", ")
      .map((x) => tags.find((y) => y.Value === x)?.Id)
      .filter((x) => x)
      .join(", ");
    this.activityTypeId = email.ActivityTypeID?.toString();
  }
}

const getDisplayName = (fromName?: string, fromEmail?: string) => {
  if (fromName && fromName.trim()) return fromName.trim();
  if (!fromEmail) return "";
  return fromEmail.split("@")[0];
};

const normalizeList = (v?: string | string[]) => {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const listToCommaString = (arr?: Array<string | number>) =>
  (arr ?? []).filter(Boolean).map(String).join(", ");

export const EmailEdit = ({
  email,
  onSubmit = undefined,
  actions
}: {
  email: ActivityGetResponse.Item;
  onSubmit?: () => void;
  actions?: any;
}) => {
  const [emailEdit, setEmailEdit] = useState(new EmailEditModel(email));
  const [isSaving, setIsSaving] = useState(false);
  const [mailEdited, setMailEdited] = useState(false);
  const [showRecipientsDetails, setShowRecipientsDetails] = useState(false);
  const [viewHistory, setViewHistory] = useState<MailViewUser[] | null>(null);
  const [viewHistoryLoading, setViewHistoryLoading] = useState(false);
  const [viewHistoryOpen, setViewHistoryOpen] = useState(false);

  const groupName: string =
    email.Group ??
    (email.GroupID &&
      StorageService.getCache(CacheEntity.GroupName, email.GroupID));
  const hasGroupName = CompanyService.getGroupName()?.length > 0;

  const { translate } = TranslationService;
  const { showToast } = useContext(ToastContext);

  // const defaultActivityType = CompanyService.getActivityTypes()
  //     .filter(x => x.Mail).find(x => x.MailDefault)?.ActivityTypeID;

  const updateEmail =
    (propName: string) => (value: string | boolean | undefined | number) => {
      setMailEdited(true);
      setEmailEdit((prev) => ({ ...prev, [propName]: value }));
    };

  const selectedTagsArray = useMemo(
    () => normalizeList(emailEdit.tags),
    [emailEdit.tags]
  );
  const selectedActivityType =
    emailEdit.activityTypeId ?? (email as any).ActivityTypeID ?? "";

  const fromName = useMemo(
    () => getDisplayName(email?.FromName as any, (email as any)?.From),
    [email]
  );
  const dateStr = useMemo(
    () =>
      emailEdit.date
        ? formatDateTimeDigits(restTimezone(new Date(emailEdit.date)))
        : "-",
    [emailEdit.date]
  );

  const groupSelectOptions = async (inputValue: string) => {
    let list: Array<{ value: string; label: string }> = [];
    const minInputLength = 2;
    if (!email.PersonID || inputValue.length < minInputLength) {
      return list;
    }
    const result = await GroupService.search(
      email.PersonID.toString(),
      inputValue
    );
    if (!(result instanceof Error)) {
      list = result.list.map((x) => ({
        value: x.data.toString(),
        label: x.value
      }));
    }
    return list;
  };

  const canSubmit = () => !isSaving && !!emailEdit.date;

  const submit = async () => {
    if (!canSubmit()) return;
    setIsSaving(true);
    const result = await ActivityService.editEmail(
      emailEdit.id,
      emailEdit.date,
      emailEdit.activityTypeId,
      emailEdit.tags,
      emailEdit.groupId
    );
    if (result instanceof Error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
      setIsSaving(false);
    } else {
      showToast(translate.SavedChanges, undefined, "success");
      onSubmit && onSubmit();
      setEmailEdit(new EmailEditModel(email));
      setIsSaving(false);
      FloatingPanelService.hidePanel();
    }
  };

  const handleEyeClick = async () => {
    setViewHistoryOpen((o) => !o);
    if (viewHistory === null) {
      setViewHistoryLoading(true);
      const ret = await ActivityService.getMailViewHistory(emailEdit.id);
      setViewHistory(ret instanceof Error ? [] : ret);
      setViewHistoryLoading(false);
    }
  };

  const toList = normalizeList((email as any).To);
  const ccList = normalizeList((email as any).CC);
  const bccList = normalizeList((email as any).BCC);

  return (
    <div className="floatingPanelW">
      <div className="floatingBodyW p-4">
        <div className="row mb-2">
          <div className="col-12">
            <h5 className="mb-0 fw-bold" style={{ lineHeight: 1.2 }}>
              {(email as any).Subject}
            </h5>
          </div>
        </div>

        <div className="row mb-2">
          <div className="d-flex  justify-content-between align-items-start flex-wrap">
            <div className="" style={{ flex: 1 }}>
              <div className="" style={{ flex: 1 }}>
                <span className="fw-bold">{fromName}</span>{" "}
                <span className="text-muted">
                  &lt;{(email as any).From}&gt;
                </span>
              </div>
              <div className="" style={{ flex: 1 }}>
                {toList.length > 0 && (
                  <>
                    {translate.To + " "} {toList.join(", ")}
                    {(ccList.length > 0 || bccList.length > 0) && (
                      <button
                        className="btn btn-link btn-sm p-0 align-baseline ms-1"
                        title="Ver CC/CCO"
                        onClick={() => setShowRecipientsDetails((v) => !v)}
                      >
                        <i
                          className={`fas fa-chevron-${showRecipientsDetails ? "up" : "down"}`}
                        />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="d-flex  align-items-center text-nowrap mt-2 mt-sm-0">
              {email.files.length > 0 ? (
                <TooltipComponent
                  title={TranslationService.translate.AttachedFile}
                >
                  <i className="far fa-fw fa-paperclip activity-icon-color my-2" />
                </TooltipComponent>
              ) : null}
              {CompanyService.canDo("editmail") ? (
                <div className="ms-2" style={{ minWidth: "200px" }}>
                  <DateTimePickerEditor
                    onChange={(dateStr) => {
                      if (dateStr) {
                        const date = new Date(dateStr);
                        if (!isNaN(date.getTime())) {
                          updateEmail("date")(date.toString());
                        }
                      }
                    }}
                    defaultValue={
                      emailEdit.date
                        ? OptionalMap(
                            restTimezone(parseDate(emailEdit.date)),
                            (x) => addTimezone(x).toJSON()
                          )
                        : undefined
                    }
                  />
                </div>
              ) : (
                <span className="text-muted small">{dateStr}</span>
              )}
            </div>
          </div>
        </div>

        {showRecipientsDetails && (
          <div className="row mb-2 small text-muted">
            <div className="col-12">
              {ccList.length > 0 && (
                <div className="mb-1">
                  <span className="me-1">CC:</span>
                  <span>{ccList.join(", ")}</span>
                </div>
              )}
              {bccList.length > 0 && (
                <div>
                  <span className="me-1">CCO:</span>
                  <span>{bccList.join(", ")}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="my-4">
          <SafeHtml html={(email as any).Content} className="card-body" />
        </div>

        {DisplayFileIcon(
          email as unknown as ActivityPendingResponse.Mail,
          ActivityHistoryType.email,
          true
        )}
      </div>

      {CompanyService.canDo("editmail") ? (
        <div className="floatingFooterW">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center">
              {actions &&
                actions.map((action, index) => {
                  return (
                    <TooltipComponent key={index} title={action.tooltip}>
                      <button
                        className="btn btn-link"
                        onClick={() => action.onClick()}
                      >
                        <DynamicIcon icon={action.icon} />
                      </button>
                    </TooltipComponent>
                  );
                })}
              {CompanyService.canDo("edittag") && (
                <TagsIconDropdown
                  title={translate.Tags}
                  value={selectedTagsArray}
                  onChange={(arr) => {
                    updateEmail("tags")(listToCommaString(arr));
                  }}
                />
              )}

              {CompanyService.canDo("editmail") && (
                <ActivityTypeIconDropdown
                  title={translate.ActivityType}
                  value={selectedActivityType}
                  onChange={(val) => {
                    updateEmail("activityTypeId")(val ?? undefined);
                  }}
                />
              )}

              {hasGroupName && CompanyService.canDo("editmail") && (
                <GroupIconDropdown
                  title={CompanyService.getGroupName()}
                  value={emailEdit.groupId as string | undefined}
                  defaultVal={
                    (email as any).GroupID && groupName
                      ? { value: (email as any).GroupID, label: groupName }
                      : undefined
                  }
                  loadOptions={groupSelectOptions}
                  onChange={(val) => updateEmail("groupId")(val)}
                />
              )}

              {CompanyService.getIsAdmin() && (
                <MailViewHistoryDropdown
                  open={viewHistoryOpen}
                  onClose={() => setViewHistoryOpen(false)}
                  trigger={
                    <button
                      className="btn btn-link mail-view-history-btn"
                      onClick={handleEyeClick}
                      title={translate.SeenBy}
                      disabled={viewHistoryLoading}
                      aria-haspopup="menu"
                      aria-expanded={viewHistoryOpen}
                    >
                      {viewHistoryLoading ? (
                        <i className="fas fa-spinner-third fa-spin"></i>
                      ) : (
                        <i className="far fa-eye"></i>
                      )}
                    </button>
                  }
                >
                  {viewHistoryLoading && (
                    <div className="mail-view-history-loading">
                      <i className="fas fa-spinner-third fa-spin"></i>
                    </div>
                  )}
                  {!viewHistoryLoading && (
                    <>
                      <div className="mail-view-history-header">
                        {translate.SeenBy} ({viewHistory?.length ?? 0})
                      </div>
                      {viewHistory?.map((item) => {
                        const initials = item.UserFullname.split(" ")
                          .slice(0, 2)
                          .map((w) => w[0])
                          .join("")
                          .toUpperCase();
                        const date = formatDateTimeDigits(
                          restTimezone(new Date(item.Date))
                        );
                        return (
                          <div
                            key={item.UserId}
                            className="mail-view-history-item"
                          >
                            <span className="mail-view-history-avatar">
                              {initials}
                            </span>
                            <span className="mail-view-history-name">
                              {item.UserFullname}
                            </span>
                            <span className="mail-view-history-date">
                              {date}
                            </span>
                          </div>
                        );
                      })}
                    </>
                  )}
                </MailViewHistoryDropdown>
              )}
            </div>

            <div className="d-flex align-items-center gap-2">
              {mailEdited && (
                <button className="btn btn-primary" onClick={submit}>
                  {translate.Save}
                  {isSaving && (
                    <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
